/* istanbul ignore file */
import { SafeHtml } from '@angular/platform-browser';

import { DropDownFilterSettings, VirtualizationSettings } from '@progress/kendo-angular-dropdowns';
import { DOMOutputSpec, NodeSpec } from 'prosemirror-model';

import { ActionTypeDropdownOption, BooleanDropdownValue, FieldIdentifier, LogicBuilderFormModel, PresetStyleColorSelection, Project, SelectableField, Site, Trigger, TriggerOperatorDropdownOption, User } from '../../../models';
import { BasicDropdownValue } from '../../../models/basic-dropdown-value';
import { MapPinColorSelection } from '../../../models/logic-builder/map-pin-color-selection';
import { ActionType, ActionTypeIcon, ActionTypeText, CheckBoxTriggerOperatorText, DateSelectTriggerOperatorText, DefaultTriggerOperatorText, DropdownTriggerOperatorText, FieldStyleColor, FieldType, MapPinColor, NumberTriggerOperatorText, PersonTriggerOperatorText, RecipientType, TriggerOperator, TriggerRelationship, TriggerRelationshipText, UploaderTriggerOperatorText } from '../../../shared/enums';

export class LogicBuilderFormServiceModel {
    recipientToolTip = `Recipients without access to fields referenced in the logic won't receive this email. To ensure delivery, review and adjust field privileges on Admin > Roles page as needed.`;
    disabledStyleMapPinColorOtherLogicBuilderUsedTooltip = `This action is already in use in another logic configuration and can only be applied once per project.`;
    disabledPreviewTooltip = `Preview button is disabled. Complete all required fields before previewing.`;
    disabledSaveTooltip = `Save button is disabled. Complete all required fields before saving.`;
    siteAlias: string;
    projectAlias: string;
    isShowingEmailPreview = false;
    isLoadingEmailPreview = false;
    isMapPinUsed = false;
    triggersResponsible: Trigger[] = [];
    emailPreviewContent: SafeHtml = '';
    pullFieldsFromSmartsheet: string[] = [];
    setFieldsFromOtherLogicBuilders: FieldIdentifier[] = [];
    styleFieldsFromOtherLogicBuilders: FieldIdentifier[] = [];
    triggerFieldsFromOtherLogicBuilders: FieldIdentifier[] = [];
    allFields: SelectableField[] = [];
    availableActionTypes: Map<number, ActionTypeDropdownOption[]> = new Map<number, ActionTypeDropdownOption[]>();
    availableFieldsToSetValuesFrom: SelectableField[] = [];
    availableFieldsToStyle: SelectableField[] = [];
    availableFieldStyleColorOptions: Map<number, PresetStyleColorSelection[]> = new Map<number, PresetStyleColorSelection[]>();
    availableMapPinColorOptions: Map<number, MapPinColorSelection[]> = new Map<number, MapPinColorSelection[]>();
    availableSetFields: SelectableField[] = [];
    availableTriggerFields: SelectableField[] = [];
    availableEmailRecipientFields: SelectableField[] = [];
    availableEmailTemplateFields: SelectableField[] = [];
    currentProject: Project;
    currentUser: User;
    dialogWidth = `75rem`;
    doesLogicBuilderContainDynamicLogic = false;
    doesLogicBuilderContainDynamicMapPinStyling = false;
    doesLogicBuilderContainDynamicStyleFieldLogic = false;
    existingLogicBuilderTitle = `Edit`;
    fieldsThatCanBeStyled: SelectableField[] = [];
    fieldStyleColorOptions: PresetStyleColorSelection[] = [];
    filterSettings: DropDownFilterSettings = { caseSensitive: false, operator: "contains" };
    formIsSaving = false;
    logicBuilderFormModel: LogicBuilderFormModel;
    logicBuilderTitle: string;
    mapPinColorOptions: MapPinColorSelection[] = [];
    maxSupportedActionsPerLogicSet = 4;
    maxFieldStyleSupportedLogicSets = Object.keys(FieldStyleColor).length / 2;
    maxPinStyleSupportedLogicSets = Object.keys(MapPinColor).length / 2;
    maxSupportedTriggersPerLogicSet = 6;
    newLogicBuilderTitle = `Create`;
    selectedGridId: string;
    shouldAllowAddDynamicMapPinStylingLogicSet = false;
    shouldAllowAddDynamicStyleFieldLogicSet = false;
    shouldAllowHasChangedTriggerOperator = true;
    shouldAllowStyleActionTypes = true;
    shouldAllowSetActionTypes = true;
    shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
    siteDropdownInfo = new LogicBuilderSiteFormModel();
    supportedFieldsToSetValuesFrom: SelectableField[] = [];
    supportedFieldTypesForSetField: string[] = [FieldType.CheckBox, FieldType.DateSelect, FieldType.DropDown, FieldType.Number, FieldType.Person];
    supportedSetFields: SelectableField[] = [];
    triggersCount = 0;
    unNestedTriggerIndex = -1;
    users: User[] = [];
    virtualizationSettings: VirtualizationSettings = { itemHeight: 32, pageSize: 50 };
    virtualizationSettingsForFields: VirtualizationSettings = { itemHeight: 70, pageSize: 50 };
    availableDateTypeOptions: BooleanDropdownValue[] = [
        { id: true, name: 'Today' },
        { id: false, name: 'Specific Date' },
    ];

    availableRecipentTypeOptions: BasicDropdownValue[] = [
        { id: RecipientType.List, name: 'a specific person(s)' },
        { id: RecipientType.Field, name: 'a person field (dynamic)' },
    ];

    availableSetFieldCheckboxOptions: BooleanDropdownValue[] = [
        { id: true, name: 'Checked' },
        { id: false, name: 'Unchecked' },
    ];

    actionTypes: ActionTypeDropdownOption[] = [
        { id: ActionType.SendEmail, name: ActionTypeText.SendEmail, icon: ActionTypeIcon.SendEmail, isUsed: false, isEnabled: true },
        { id: ActionType.StyleFieldColor, name: ActionTypeText.StyleFieldColor, icon: ActionTypeIcon.StyleFieldColor, isUsed: false, isEnabled: true },
        { id: ActionType.SetMapPinColor, name: ActionTypeText.SetMapPinColor, icon: ActionTypeIcon.SetMapPinColor, isUsed: false, isEnabled: true },
        { id: ActionType.SetField, name: ActionTypeText.SetField, icon: ActionTypeIcon.SetField, isUsed: false, isEnabled: true },
    ];

    dateSelectTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: DateSelectTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.DateFieldIsInTheLastXDays, name: DateSelectTriggerOperatorText.DateFieldIsInTheLastXDays, isEnabled: true },
        { id: TriggerOperator.DateFieldIsInTheNextXDays, name: DateSelectTriggerOperatorText.DateFieldIsInTheNextXDays, isEnabled: true },
        { id: TriggerOperator.DateFieldIsInPast, name: DateSelectTriggerOperatorText.DateFieldIsInPast, isEnabled: true },
        { id: TriggerOperator.DateFieldIsInFuture, name: DateSelectTriggerOperatorText.DateFieldIsInFuture, isEnabled: true },
        { id: TriggerOperator.HasValue, name: DateSelectTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: DateSelectTriggerOperatorText.NoValue, isEnabled: true },
        { id: TriggerOperator.LessThan, name: DateSelectTriggerOperatorText.LessThan, isEnabled: true },
        { id: TriggerOperator.GreaterThan, name: DateSelectTriggerOperatorText.GreaterThan, isEnabled: true },
        { id: TriggerOperator.Equal, name: DateSelectTriggerOperatorText.EqualTo, isEnabled: true },
        { id: TriggerOperator.GreaterThanOrEqualTo, name: DateSelectTriggerOperatorText.GreaterThanOrEqualTo, isEnabled: true },
        { id: TriggerOperator.LessThanOrEqualTo, name: DateSelectTriggerOperatorText.LessThanOrEqualTo, isEnabled: true },
    ];

    checkBoxTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasValue, name: CheckBoxTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: CheckBoxTriggerOperatorText.NoValue, isEnabled: true },
        { id: TriggerOperator.HasChanged, name: CheckBoxTriggerOperatorText.HasChanged, isEnabled: true },
    ];

    dropdownTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: DropdownTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.Equal, name: DropdownTriggerOperatorText.Equal, isEnabled: true },
        { id: TriggerOperator.NotEqual, name: DropdownTriggerOperatorText.NotEqual, isEnabled: true },
        { id: TriggerOperator.HasValue, name: DropdownTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: DropdownTriggerOperatorText.NoValue, isEnabled: true },
    ];

    personTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: PersonTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.Equal, name: PersonTriggerOperatorText.Equal, isEnabled: true },
        { id: TriggerOperator.HasValue, name: PersonTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: PersonTriggerOperatorText.NoValue, isEnabled: true },
    ];

    uploaderTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: UploaderTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.HasValue, name: UploaderTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: UploaderTriggerOperatorText.NoValue, isEnabled: true },
    ];

    numberTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: NumberTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.HasValue, name: NumberTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: NumberTriggerOperatorText.NoValue, isEnabled: true },
        { id: TriggerOperator.GreaterThan, name: NumberTriggerOperatorText.GreaterThan, isEnabled: true },
        { id: TriggerOperator.LessThan, name: NumberTriggerOperatorText.LessThan, isEnabled: true },
        { id: TriggerOperator.Equal, name: NumberTriggerOperatorText.EqualTo, isEnabled: true },
        { id: TriggerOperator.GreaterThanOrEqualTo, name: NumberTriggerOperatorText.GreaterThanOrEqualTo, isEnabled: true },
        { id: TriggerOperator.LessThanOrEqualTo, name: NumberTriggerOperatorText.LessThanOrEqualTo, isEnabled: true },
    ];

    defaultTriggerOperators: TriggerOperatorDropdownOption[] = [
        { id: TriggerOperator.HasChanged, name: DefaultTriggerOperatorText.HasChanged, isEnabled: true },
        { id: TriggerOperator.HasValue, name: DefaultTriggerOperatorText.HasValue, isEnabled: true },
        { id: TriggerOperator.NoValue, name: DefaultTriggerOperatorText.NoValue, isEnabled: true },
    ];

    availableRelationships: BasicDropdownValue[] = [
        { id: TriggerRelationship.And, name: TriggerRelationshipText.And },
        { id: TriggerRelationship.Or, name: TriggerRelationshipText.Or },
    ];

    comparisonInputValueFieldTypes: Map<string, Map<number, string>> = new Map<string, Map<number, string>>([
        [FieldType.DateSelect, new Map<number, string>([
            [TriggerOperator.DateFieldIsInTheNextXDays, FieldType.Number],
            [TriggerOperator.DateFieldIsInTheLastXDays, FieldType.Number],
            [TriggerOperator.LessThan, FieldType.DateSelect],
            [TriggerOperator.GreaterThan, FieldType.DateSelect],
            [TriggerOperator.Equal, FieldType.DateSelect],
            [TriggerOperator.LessThanOrEqualTo, FieldType.DateSelect],
            [TriggerOperator.GreaterThanOrEqualTo, FieldType.DateSelect],
        ])],
        [FieldType.DropDown, new Map<number, string>([
            [TriggerOperator.Equal, FieldType.DropDown],
            [TriggerOperator.NotEqual, FieldType.DropDown],
        ])],
        [FieldType.PointCategory, new Map<number, string>([
            [TriggerOperator.Equal, FieldType.PointCategory],
            [TriggerOperator.NotEqual, FieldType.PointCategory],
        ])],
        [FieldType.Person, new Map<number, string>([
            [TriggerOperator.Equal, FieldType.Person],
        ])],
        [FieldType.Number, new Map<number, string>([
            [TriggerOperator.Equal, FieldType.Number],
            [TriggerOperator.LessThan, FieldType.Number],
            [TriggerOperator.GreaterThan, FieldType.Number],
            [TriggerOperator.LessThanOrEqualTo, FieldType.Number],
            [TriggerOperator.GreaterThanOrEqualTo, FieldType.Number],
        ])],
    ]);

    replaceableFieldNode: NodeSpec = {
        inline: true,
        group: 'inline',
        atom: true,
        selectable: true,
        attrs: {
            id: {},
            class: { default: 'khts-html-replaceable-field' },
            text: { default: '' },
            title: { default: '' },
        },
        parseDOM: [{
            tag: 'span',
            getAttrs: dom => ({
                id: (dom as HTMLElement).getAttribute('id'),
                class: (dom as HTMLElement).getAttribute('class'),
                text: (dom as HTMLElement).innerText || (dom as HTMLElement).textContent || '',
                title: (dom as HTMLElement).getAttribute('title'),
            }),
        }],
        toDOM(node): DOMOutputSpec {
            const { id, class: cls, title, text } = node.attrs;
            return ['span', { id, class: cls, title }, text];
        },
    };
}

export class LogicBuilderSiteFormModel {
    numberOfSelectedSites = 0;
    availableSites: Site[] = [];
    filteredSites: Site[] = [];
    modeledSites: string[] = [];
    selectAllSite: Site = new Site();
    selectAllSiteNo = '-1';
    selectAllMessage = `All `;
    selectedSites: string[] = [];
    showCustomSitePlaceholder = true;
}
